
import React, { useState } from "react";
import { NewsletterAnalytics } from "./NewsletterAnalytics";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DeviceAnalytics } from "./analytics/DeviceAnalytics";
import { GeographicAnalytics } from "./analytics/GeographicAnalytics";
import { TimeAnalytics } from "./analytics/TimeAnalytics";
import { useEmailAnalytics } from "@/hooks/useEmailAnalytics";

interface DashboardAnalyticsProps {
  campaignId?: string;
  detailed?: boolean;
}

export function DashboardAnalytics({ campaignId, detailed = false }: DashboardAnalyticsProps) {
  const [activeTab, setActiveTab] = useState<string>(detailed ? "detailed" : "overview");
  const { useDeviceData, useGeographicData } = useEmailAnalytics({
    campaignId: campaignId,
  });
  
  const { data: deviceData, isLoading: deviceLoading } = useDeviceData();
  const { data: geoData, isLoading: geoLoading } = useGeographicData();

  return (
    <div className="space-y-4">
      {detailed ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="detailed">Engagement</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview" className="mt-4">
            <NewsletterAnalytics initialCampaignId={campaignId} focusDetailed={false} />
          </TabsContent>
          
          <TabsContent value="detailed" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <DeviceAnalytics deviceData={deviceData} isLoading={deviceLoading} />
              <TimeAnalytics isLoading={false} />
            </div>
            <GeographicAnalytics geoData={geoData} isLoading={geoLoading} />
          </TabsContent>
          
          <TabsContent value="advanced" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Advanced Analytics</CardTitle>
                <CardDescription>Coming in Phase 4</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-muted-foreground">
                    Advanced analytics features will include:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                    <li>Predictive engagement scoring</li>
                    <li>A/B test performance comparisons</li>
                    <li>Subscriber journey mapping</li>
                    <li>Content performance analysis</li>
                    <li>Automated insights and recommendations</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <NewsletterAnalytics initialCampaignId={campaignId} focusDetailed={false} />
      )}
    </div>
  );
}
